import { CONTACT_STATUS, ContactStatus, ContactType } from '@src/api/types/contact';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  general: 'サイト全般',
  support_service: 'サービス',
  other: 'その他',
};

export const CONTACT_TYPE_BADGE_CLASSES: Record<ContactType, string> = {
  general: 'border-primary-500 text-primary-500',
  support_service: 'border-contact-type-support-service text-contact-type-support-service',
  other: 'border-gray-400 text-gray-450',
};

export function ContactTypeBadge({ type }: { type: ContactType }) {
  return (
    <span
      className={`inline-block rounded-full border border-solid bg-white px-3 py-1 text-xs ${CONTACT_TYPE_BADGE_CLASSES[type]}`}
    >
      {CONTACT_TYPE_LABELS[type]}
    </span>
  );
}

export const STATUS_LABELS: Record<ContactStatus, string> = {
  1: '未対応',
  2: '対応中',
  3: '完了',
};

export const STATUS_DOT_CLASSES: Record<ContactStatus, string> = {
  1: 'bg-contact-status-unhandled',
  2: 'bg-contact-status-in-progress',
  3: 'bg-contact-status-done',
};

export const STATUS_OPTIONS: ContactStatus[] = [
  CONTACT_STATUS.UNHANDLED,
  CONTACT_STATUS.IN_PROGRESS,
  CONTACT_STATUS.DONE,
];

export function ContactStatusDropdown({
  status,
  disabled = false,
  onChange,
}: {
  status: ContactStatus;
  disabled?: boolean;
  onChange: (status: ContactStatus) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleSelect = async (option: ContactStatus) => {
    if (option === status) {
      setIsOpen(false);
      return;
    }

    await onChange(option);
    setIsOpen(false);
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    option: ContactStatus,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(option);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={triggerRef}
      className='relative inline-block w-[110px]'
    >
      <button
        type='button'
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className='flex w-full items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60'
      >
        <span className='flex items-center gap-2'>
          <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT_CLASSES[status]}`} />
          {STATUS_LABELS[status]}
        </span>
        <div
          className='h-[5px] w-[10px] shrink-0 bg-gray-400'
          style={{ clipPath: 'polygon(50% 100%, 0 10%, 100% 10%)' }}
        />
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={listRef}
            role='listbox'
            className='fixed z-20 overflow-hidden rounded-md border border-gray-200 bg-white text-sm'
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            {STATUS_OPTIONS.map((option) => (
              <div
                key={option}
                role='option'
                tabIndex={0}
                aria-selected={option === status}
                onClick={() => handleSelect(option)}
                onKeyDown={(event) => handleOptionKeyDown(event, option)}
                className={`flex cursor-pointer items-center gap-2 px-3 py-[10px] transition-colors hover:bg-primary-500 hover:text-white ${
                  option === status ? 'bg-gray-50' : ''
                }`}
              >
                <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT_CLASSES[option]}`} />
                {STATUS_LABELS[option]}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
